import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const FreePreviewModal = ({ isOpen, onClose, onSuccess }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;
  
  const [formData, setFormData] = useState({
    state: '',
    zip_code: '',
    email: '',
    honeypot: '' // Bot detection
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.state) {
      newErrors.state = 'Please select a state';
    }

    if (!formData.zip_code) {
      newErrors.zip_code = 'ZIP code is required';
    } else if (!/^\d{5}$/.test(formData.zip_code)) {
      newErrors.zip_code = 'ZIP code must be exactly 5 digits';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check - if filled, it's a bot
    if (formData.honeypot) {
      console.log('Bot detected via honeypot');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/free-preview/generate`, {
        state: formData.state,
        zip_code: formData.zip_code,
        email: formData.email
      });

      // Track conversion with Google Analytics
      if (window.gtag) {
        window.gtag('event', 'lead_generated', {
          method: 'free_preview',
          state: formData.state,
          source: 'facebook_ad'
        });
      }

      // Track conversion with Facebook Pixel
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'free_preview_100_leads',
          state: formData.state,
          value: 0,
          currency: 'USD'
        });
      }

      toast.success('Check your email! Your list is on the way.');
      
      // Close modal and redirect to success page
      onClose();
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Redirect to success page
      window.location.href = `/free-preview-success?email=${encodeURIComponent(formData.email)}&state=${formData.state}&zip=${formData.zip_code}`;

    } catch (error) {
      console.error('Form submission error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to generate preview. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
          disabled={loading}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Get Your Free Preview
          </h2>
          <p className="text-slate-600 text-sm sm:text-base">
            100 verified homeowners sent to your email instantly
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field (hidden from users) */}
          <input
            type="text"
            name="website"
            value={formData.honeypot}
            onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
            style={{ position: 'absolute', left: '-9999px' }}
            tabIndex="-1"
            autoComplete="off"
          />

          {/* State Dropdown */}
          <div>
            <Label htmlFor="state" className="text-slate-700 font-medium">
              Where are you prospecting?
            </Label>
            <select
              id="state"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              className={`mt-1 w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.state ? 'border-red-500' : 'border-slate-300'
              }`}
              disabled={loading}
            >
              <option value="">Select a state</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
          </div>

          {/* ZIP Code */}
          <div>
            <Label htmlFor="zip_code" className="text-slate-700 font-medium">
              ZIP Code
            </Label>
            <Input
              id="zip_code"
              type="text"
              placeholder="74120"
              maxLength={5}
              value={formData.zip_code}
              onChange={(e) => setFormData({ ...formData, zip_code: e.target.value.replace(/\D/g, '') })}
              className={`mt-1 ${errors.zip_code ? 'border-red-500' : ''}`}
              disabled={loading}
            />
            {errors.zip_code && <p className="text-red-500 text-sm mt-1">{errors.zip_code}</p>}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-slate-700 font-medium">
              What's your work email?
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@yourcompany.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`mt-1 ${errors.email ? 'border-red-500' : ''}`}
              disabled={loading}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
          >
            {loading ? 'Generating Your List...' : 'Download My Free List →'}
          </Button>

          {/* Subtext */}
          <p className="text-center text-xs text-slate-500">
            No password required • Instant download
          </p>
        </form>
      </div>
    </div>
  );
};

export default FreePreviewModal;
